import { useState, type FormEvent } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useDiagnostic } from '@/contexts/DiagnosticContext';
import { Input } from '@/domains/shared/components/Input';
import { Button } from '@/domains/shared/components/Button';
import { formatPhoneNumber } from '@/domains/shared/utils/formatters';

export function IdentificationPage() {
  const { navigate } = useRouter();
  const { leadInfo, setLeadInfo } = useDiagnostic();

  const [name, setName] = useState(leadInfo.name || '');
  const [email, setEmail] = useState(leadInfo.email || '');
  const [phone, setPhone] = useState(leadInfo.phone || '');
  const [companyName, setCompanyName] = useState(leadInfo.companyName || '');
  const [companySize, setCompanySize] = useState(leadInfo.companySize || '2 a 5 pessoas');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!name.trim()) {
      errs.name = 'Por favor, informe seu nome.';
    }
    if (!email.trim() || !email.includes('@')) {
      errs.email = 'Informe um e-mail válido.';
    }
    const rawPhoneDigits = phone.replace(/\D/g, '');
    if (rawPhoneDigits.length < 10) {
      errs.phone = 'Informe seu número com DDD (ex: 11 98765-4321).';
    }
    if (!companyName.trim()) {
      errs.companyName = 'Informe o nome do seu escritório contábil.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLeadInfo({
      name,
      email,
      phone,
      companyName,
      companySize,
    });

    navigate('/consentimento');
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/diagnostico')}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-semibold text-brand-offWhite/80 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded-lg px-2"
          >
            <span>←</span> Voltar às perguntas
          </button>
          <span className="text-xs font-bold text-brand-orange">Etapa Quase Final</span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-4 py-8 sm:py-12">
        <div className="text-left">
          <span className="inline-block rounded bg-brand-orange/20 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange">
            Identificação
          </span>
          <h1 className="mt-3 text-2xl font-black text-white sm:text-3xl">
            Onde devemos entregar seu diagnóstico?
          </h1>
          <p className="mt-2 text-sm text-brand-offWhite/80">
            Seus dados são confidenciais e serão usados exclusivamente para personalizar a devolutiva e direcionar as orientações para a realidade da sua empresa.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4 text-left" noValidate>
          <Input
            label="Seu Nome Completo"
            placeholder="Ex: Carlos Eduardo Mendes"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            required
          />

          <Input
            label="Seu E-mail Profissional"
            type="email"
            placeholder="Ex: carlos@mendescontabilidade.com.br"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />

          <Input
            label="WhatsApp (com DDD)"
            type="tel"
            placeholder="(11) 98765-4321"
            value={phone}
            onChange={handlePhoneChange}
            error={errors.phone}
            required
          />

          <Input
            label="Nome do Escritório / Empresa Contábil"
            placeholder="Ex: Mendes Contabilidade Consultiva"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            error={errors.companyName}
            required
          />

          {/* Tamanho da Equipe */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="company-size-select" className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/80">
              Porte / Tamanho da Equipe
            </label>
            <select
              id="company-size-select"
              value={companySize}
              onChange={(e) => setCompanySize(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-navy-light/60 px-4 py-3 text-sm text-white shadow-inner focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/30 min-h-[44px]"
            >
              <option value="Só eu" className="bg-navy">Só eu (autônomo / início)</option>
              <option value="2 a 5 pessoas" className="bg-navy">2 a 5 pessoas</option>
              <option value="6 a 15 pessoas" className="bg-navy">6 a 15 pessoas</option>
              <option value="Mais de 15 pessoas" className="bg-navy">Mais de 15 pessoas (grande porte)</option>
            </select>
          </div>

          <div className="mt-8 pt-4">
            <Button type="submit" variant="primary" size="lg" className="w-full text-base font-bold shadow-lg">
              Continuar para Termos e Envio →
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
